import moment from "moment";
import { ROLE, TimeUnit } from "./enums";
import { toaster } from "./toast";
import { RecurrenceFrequency, RecurrencePattern, RecurrenceWeekOfMonth } from "./enums";
export function debounce(func, delay) {
    let timeoutId;

    return function (...args) {
        clearTimeout(timeoutId);

        timeoutId = setTimeout(() => {
            func.apply(this, args);
        }, delay);
    };
}

export function debounceAsync(func, delay) {
    let timeoutId;
    let lastPromise;

    return function (...args) {
        clearTimeout(timeoutId);

        return new Promise((resolve, reject) => {
            const later = async () => {
                timeoutId = null;
                try {
                    const result = await func.apply(this, args);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            };

            timeoutId = setTimeout(later, delay);

            if (lastPromise) {
                lastPromise.catch(() => {}); // Ignore any previous pending promise
            }
            lastPromise = new Promise((resolve) => {
                resolve(); // Resolving the promise immediately after setting the timeout
            });
        });
    };
}

export const convertToMilliSeconds = (delayValue, delayUnit) => {
    const conversionFactors = {
        [TimeUnit.SECONDS]: 1,
        [TimeUnit.MINUTES]: 60,
        [TimeUnit.HOURS]: 3600,
        [TimeUnit.DAYS]: 86400
    };

    return delayValue * conversionFactors[delayUnit] * 1000;
};


export const formatDate = (created_at, timeZone) => {
    if (!created_at) return '';
    if (timeZone) {
        try {
            const d = new Date(created_at);
            if (isNaN(d.getTime())) return created_at;
            const dtf = new Intl.DateTimeFormat('en-US', {
                timeZone: timeZone || 'UTC',
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
            return dtf.format(d);
        } catch (e) {
            return new Date(created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        }
    }
    return new Date(created_at).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
};


export function getMonthName(monthNumber) {
    const date = new Date(null, monthNumber - 1);
    return date.toLocaleString('en', { month: 'long' });
}

export const utcToLocalString = (date, timeZone) => {
    if (!date) return '';
    if (timeZone) {
        return utcToZonedString(date, timeZone);
    }
    return moment.utc(date).local().format('YYYY-MM-DDTHH:mm');
}

export const utcToLocalStringWithoutT = (date, timeZone) => {
    if (!date) return '';
    if (timeZone) {
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return date;
            const dtf = new Intl.DateTimeFormat('en-US', {
                timeZone: timeZone || 'UTC',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
            const parts = dtf.formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
            const dayPeriod = parts.dayPeriod || '';
            return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute} ${dayPeriod}`.trim();
        } catch (e) {
            return moment.utc(date).local().format('YYYY-MM-DD hh:mm A');
        }
    }
    return moment.utc(date).local().format('YYYY-MM-DD hh:mm A');
}

export const localeToUtcString = (date, timeZone) => {
    if (!date) return '';
    if (timeZone) {
        return zonedLocalToUtcString(date, timeZone);
    }
    return moment(date).utc().format('YYYY-MM-DDTHH:mm');
}

export const getNowInTimezone = (timeZone = 'UTC') => {
    const tz = timeZone || 'UTC';
    try {
        const dtf = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const parts = dtf.formatToParts(new Date()).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});
        const hour = parts.hour === '24' ? '00' : parts.hour;
        return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
    } catch (e) {
        return new Date().toISOString().slice(0, 16);
    }
}

export const utcToZonedString = (utcDate, timeZone = 'UTC') => {
    if (!utcDate) return '';
    const tz = timeZone || 'UTC';
    try {
        const dStr = typeof utcDate === 'string' && !utcDate.endsWith('Z') && !utcDate.includes('+') ? `${utcDate}Z` : utcDate;
        const dateObj = new Date(dStr);
        if (isNaN(dateObj.getTime())) return '';
        const dtf = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
        const parts = dtf.formatToParts(dateObj).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});
        const hour = parts.hour === '24' ? '00' : parts.hour;
        return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
    } catch (e) {
        return '';
    }
}

export const zonedLocalToUtcString = (dateTimeStr, timeZone = 'UTC') => {
    if (!dateTimeStr) return '';
    const tz = timeZone || 'UTC';
    if (tz === 'UTC') return dateTimeStr;
    try {
        const [datePart, timePart] = dateTimeStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        const naiveUtc = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
        const dtf = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        const parts = dtf.formatToParts(naiveUtc).reduce((acc, p) => {
            acc[p.type] = p.value;
            return acc;
        }, {});
        const asUtc = Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            Number(parts.hour) === 24 ? 0 : Number(parts.hour),
            Number(parts.minute),
            Number(parts.second)
        );
        const offsetMinutes = (asUtc - naiveUtc.getTime()) / 60000;
        const trueUtc = new Date(naiveUtc.getTime() - offsetMinutes * 60000);
        const y = trueUtc.getUTCFullYear();
        const m = String(trueUtc.getUTCMonth() + 1).padStart(2, '0');
        const d = String(trueUtc.getUTCDate()).padStart(2, '0');
        const h = String(trueUtc.getUTCHours()).padStart(2, '0');
        const min = String(trueUtc.getUTCMinutes()).padStart(2, '0');
        return `${y}-${m}-${d}T${h}:${min}`;
    } catch (e) {
        return dateTimeStr;
    }
}

export const utcToLocalStringWithTime = (date, timeZone) => {
    if (!date) return '';
    if (timeZone) {
        try {
            const d = new Date(date);
            if (isNaN(d.getTime())) return date;
            const dtf = new Intl.DateTimeFormat('en-US', {
                timeZone: timeZone || 'UTC',
                month: 'short',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
            const parts = dtf.formatToParts(d).reduce((acc, p) => {
                acc[p.type] = p.value;
                return acc;
            }, {});
            const dayPeriod = parts.dayPeriod || '';
            return `${parts.month} ${parts.day}, ${parts.year} ${parts.hour}:${parts.minute} ${dayPeriod}`.trim();
        } catch (e) {
            return moment(date).format('MMM DD, YYYY HH:mm A');
        }
    }
    return moment(date).format('MMM DD, YYYY HH:mm A');
}

export const formatAuditTimestamp = (date, timeZone) => {
    if (!date) return '';
    try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        const options = {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        };
        if (timeZone) {
            options.timeZone = timeZone;
        }
        return new Intl.DateTimeFormat('en-US', options).format(d);
    } catch (e) {
        return moment(date).format('MMM DD, YYYY hh:mm A');
    }
};

export const openInNewTab = (url) => {
    window.open(url, '_blank');
}

export const formatDateToYYYYMMDDTHHMM = (date = (new Date())) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export const decodeJwt = (token) => {
    try {
        const [, payloadEncoded] = token.split('.');
        // const header = JSON.parse(atob(headerEncoded));
        const payload = JSON.parse(atob(payloadEncoded));
        return payload;
    } catch (error) {
        return null
    }
}


export const decodeMilliseconds = (milliseconds) => {
    const duration = moment.duration(milliseconds);
    let value, unit;
    if (duration.asDays() >= 1) {
        value = duration.asDays();
        unit = TimeUnit.DAYS;
    } else if (duration.asHours() >= 1) {
        value = duration.asHours();
        unit = TimeUnit.HOURS;
    } else if (duration.asMinutes() >= 1) {
        value = duration.asMinutes();
        unit = TimeUnit.MINUTES;
    } else {
        value = 0;
        unit = '';
    }
    value = parseFloat(value.toFixed(2));
    return { value, unit };
}


export const setLocationIdInLocalStorage = (orgLocations = []) => {
    const localStorageLocationId = localStorage.getItem('locationId')
    const idArray = orgLocations?.map((item) => +item.id);
    let locationId = ''
    if (localStorageLocationId && idArray.includes(+localStorageLocationId)) {
        locationId = localStorageLocationId
    } else if (orgLocations.length > 0) {
        locationId = orgLocations[0].id
    }
    localStorage.setItem('locationId', locationId);
    return locationId
}

export const columnNameToLabel = (columnName) => {
  const label = columnName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
  return label;
};

export const downloadCsv = (FieldList) => {
    const predefinedFields = FieldList?.predefinedFields?.map((fields) => {
      return [fields.name];
    });
  
    const customFields = FieldList?.customFields?.map((fields) => {
      return [fields.name];
    });
    const csvColumns = [...predefinedFields, ...customFields];
    const transposedCsvData = csvColumns[0].map((_, colIndex) =>
      csvColumns.map((row) => row[colIndex])
    );
    const csvContent =
      "data:text/csv;charset=utf-8," +
      transposedCsvData.map((row) => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "CSV-File-Format");
    document.body.appendChild(link);
    link.click();
  };

  export const copyToClipboard = (value) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        toaster({success: true, message: 'Copied'})
      })
      .catch((err) => {});
  };

  export const goToDashboard = (navigate, role_id) => {
    if (role_id === ROLE.SUPER_ADMIN || role_id === ROLE.SUPER_ADMIN_STAFF) {
      localStorage.setItem('expandedItems', JSON.stringify(["/Admin/dashboard"]));
      navigate("/Admin/dashboard");
    } else {
      localStorage.setItem('expandedItems', JSON.stringify(["org/dashboard"]));
      navigate("/org/dashboard");
    }
  };

  // NEW: computes the actual next occurrence date+time for a recurring campaign,
// based on frequency (weekly/monthly), day of week/month, pattern, and time-of-day.
export const computeNextRecurrenceDate = (values) => {
  const [hours, minutes] = (values.recurrenceTime || '00:00').split(':').map(Number);
  const tz = values.recurrenceTimezone || 'UTC';
  const nowTzStr = getNowInTimezone(tz);
  const [nowDatePart, nowTimePart] = nowTzStr.split('T');
  const [nowY, nowM, nowD] = nowDatePart.split('-').map(Number);
  const [nowH, nowMin] = nowTimePart.split(':').map(Number);
  const now = new Date(nowY, nowM - 1, nowD, nowH, nowMin, 0);

  const setTime = (date) => {
    const d = new Date(date);
    d.setHours(hours, minutes, 0, 0);
    return d;
  };

  if (values.recurrenceFrequency === RecurrenceFrequency.WEEKLY) {
    const targetDay = Number(values.recurrenceDayOfWeek); // 0=Sunday ... 6=Saturday
    let candidate = setTime(now);
    const diff = (targetDay - now.getDay() + 7) % 7;
    candidate.setDate(now.getDate() + diff);
    if (candidate <= now) {
      candidate.setDate(candidate.getDate() + 7);
    }
    return candidate;
  }

  if (values.recurrenceFrequency === RecurrenceFrequency.MONTHLY) {
    if (values.recurrencePattern === RecurrencePattern.DAY_OF_MONTH) {
      const day = Number(values.recurrenceDayOfMonth);
      let candidate = setTime(new Date(now.getFullYear(), now.getMonth(), day));
      if (candidate <= now) {
        candidate = setTime(new Date(now.getFullYear(), now.getMonth() + 1, day));
      }
      return candidate;
    }

    if (values.recurrencePattern === RecurrencePattern.LAST_DAY_OF_MONTH) {
      let candidate = setTime(new Date(now.getFullYear(), now.getMonth() + 1, 0));
      if (candidate <= now) {
        candidate = setTime(new Date(now.getFullYear(), now.getMonth() + 2, 0));
      }
      return candidate;
    }

    if (values.recurrencePattern === RecurrencePattern.NTH_WEEKDAY) {
      const targetDay = Number(values.recurrenceDayOfWeek);
      const occurrence = values.recurrenceWeekOfMonth;

      const nthWeekdayOfMonth = (year, month, weekday, occ) => {
        if (occ === RecurrenceWeekOfMonth.LAST) {
          let d = new Date(year, month + 1, 0);
          while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
          return d;
        }
        const occNum = {
          [RecurrenceWeekOfMonth.FIRST]: 1, [RecurrenceWeekOfMonth.SECOND]: 2,
          [RecurrenceWeekOfMonth.THIRD]: 3, [RecurrenceWeekOfMonth.FOURTH]: 4,
        };
        let d = new Date(year, month, 1);
        let count = 0;
        while (true) {
          if (d.getDay() === weekday) {
            count++;
            if (count === occNum[occ]) return d;
          }
          d.setDate(d.getDate() + 1);
        }
      };

      let candidate = setTime(nthWeekdayOfMonth(now.getFullYear(), now.getMonth(), targetDay, occurrence));
      if (candidate <= now) {
        candidate = setTime(nthWeekdayOfMonth(now.getFullYear(), now.getMonth() + 1, targetDay, occurrence));
      }
      return candidate;
    }
  }

  return setTime(now);
};